import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Button } from '~/components/ui/button';
import { archiveEnrichmentJob } from '~/features/content-enrichment/services/enrichment-job-archive.service';
import { extractApiError } from '~/utils/extract-api-error';

import { useReconcileEnrichmentJob } from '../hooks/use-reconcile-enrichment-job';
import type { EnrichmentJobArchiveRefusal } from '../hooks/use-set-enrichment-job-archived';

interface StopAndArchiveDialogProps {
  /** The refusal to act on; `null` keeps the dialog closed. */
  refusal: EnrichmentJobArchiveRefusal | null;
  onClose: () => void;
  /** The owning control's archive call, re-run after a successful Stop or
   *  Reconcile so its hook keeps handling the outcome (toast, or another refusal). */
  onArchive: (jobId: string) => Promise<unknown>;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/** Audit string carried on the Stop call issued from this dialog. */
const STOP_REASON = 'Stopped from the list to archive the run';

/**
 * Shown when the server refused to archive because the run is provably
 * executing (`JOB_STILL_LIVE`, liveness OPEN). Two ways forward, then the
 * archive is retried automatically:
 *  - **Stop and archive** — the existing destructive Stop (`POST …/archive`,
 *    terminates the workflow, status becomes ARCHIVED / "Cancelled").
 *  - **Check and fix status** — the non-destructive reconcile probe; only
 *    archives when the server actually corrected the status to FAILED.
 * Not a `ConfirmDialog`: that component has a single confirm action.
 */
export function StopAndArchiveDialog({
  refusal,
  onClose,
  onArchive,
  testId = 'stop-and-archive-dialog',
}: StopAndArchiveDialogProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const reconcile = useReconcileEnrichmentJob();

  // Local Stop mutation (not the restart-guard hook) so the failure toast says
  // "stop", not "archive" — the two words mean different things on this screen.
  const stop = useMutation({
    mutationFn: (jobId: string) => archiveEnrichmentJob(jobId, { reason: STOP_REASON }),
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(jobId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('listArchive.refusal.stopFailed')));
    },
  });

  const busy = stop.isPending || reconcile.isPending;

  /** Re-run the archive and close either way — the owning hook toasts or
   *  re-opens this dialog if the run is somehow still live. */
  const archiveThenClose = (jobId: string) => {
    void onArchive(jobId).then(onClose, onClose);
  };

  const handleStop = () => {
    if (!refusal) return;
    stop.mutate(refusal.jobId, {
      onSuccess: () => {
        archiveThenClose(refusal.jobId);
      },
    });
  };

  const handleReconcile = () => {
    if (!refusal) return;
    reconcile.mutate(refusal.jobId, {
      onSuccess: (result) => {
        // Only a corrected status makes the row archivable; otherwise the hook
        // already relayed the server's explanation and we just close.
        if (result.reconciled) archiveThenClose(refusal.jobId);
        else onClose();
      },
    });
  };

  return (
    <AlertDialog
      open={refusal !== null}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('listArchive.refusal.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('listArchive.refusal.body')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid={`${testId}-cancel-button`}
            disabled={busy}
            className="cursor-pointer disabled:cursor-not-allowed"
            onClick={onClose}
          >
            {t('listArchive.refusal.cancel')}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid={`${testId}-reconcile-button`}
            isLoading={reconcile.isPending}
            disabled={busy}
            className="cursor-pointer disabled:cursor-not-allowed"
            onClick={handleReconcile}
          >
            {t('listArchive.refusal.reconcile')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            data-testid={`${testId}-stop-button`}
            isLoading={stop.isPending}
            disabled={busy}
            className="cursor-pointer bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500 disabled:cursor-not-allowed"
            onClick={handleStop}
          >
            {t('listArchive.refusal.stopAndArchive')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
